import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Typography,
  Stack,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useGetUserDensityQuery } from "../../Apis/progressApi";

export interface HourDensity {
  hour: number;
  label: string;
  count: number;
}

const HOURS: HourDensity[] = Array.from({ length: 24 }, (_, hour) => {
  const isPM = hour >= 12;
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;

  return {
    hour,
    label: `${displayHour} ${isPM ? "PM" : "AM"}`,
    count: 0,
  };
});

export default function UserDensityChart() {
  const { data = [], isLoading, isError } = useGetUserDensityQuery();

  const hourlyData = useMemo(() => {
    const map = new Map<number, number>();
    data.forEach((item) => map.set(item.hour, item.count));

    return HOURS.map((h) => ({
      ...h,
      count: map.get(h.hour) ?? 0,
    }));
  }, [data]);

  const totalUsers = useMemo(
    () => hourlyData.reduce((sum, item) => sum + item.count, 0),
    [hourlyData]
  );

  const peakHour = useMemo(
    () => hourlyData.reduce((best, item) => (item.count > best.count ? item : best), hourlyData[0]),
    [hourlyData]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" sx={{ textAlign: "center", p: 4 }}>
        No data available
      </Typography>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        overflow: "hidden",
      }}
    >
      <CardHeader
        sx={{ background: "#f8f8f8" }}
        title={
          <Typography variant="h6" sx={{ color: "#512384", fontWeight: 700 }}>
            User Density During the Day
          </Typography>
        }
      />

      <CardContent>
        <Stack direction="row" spacing={3} sx={{ mb: 3, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Users
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#512384" }}>
              {totalUsers}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Peak Hour
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#512384" }}>
              {peakHour.label} ({peakHour.count})
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <BarChart
            height={380}
            xAxis={[
              {
                scaleType: "band",
                data: hourlyData.map((item) => item.label),
                tickLabelStyle: {
                  
                  textAnchor: "middle",
                  fontSize: 10,
                },
              },
            ]}
            series={[
              {
                data: hourlyData.map((item) => item.count),
                label: "Users",
                color:"#512384",
                valueFormatter: (value) => `${value} Users`,
              },
            ]}
          />
        </Box>
      </CardContent>
    </Card>
  );
}